import Email from "@/domain/entities/emails/Email";

export interface DeepFocusOrder {
    mailIds: string[];
    displayedMailId: string | undefined;
}

const uniqueIdsOf = (mails: Email[]) => [
    ...new Set(mails.map((mail) => mail.uniqueMailvistaId)),
];

export const openDeepFocusOrder = (mails: Email[]): DeepFocusOrder => {
    const mailIds = uniqueIdsOf(mails);

    return { mailIds, displayedMailId: mailIds[0] };
};

/**
 * Un mail inconnu placé avant le dernier mail connu est un nouveau mail Focus (en tête) ; placé après, il vient de la
 * page suivante (en fin). Si le mail affiché sort de la liste, on affiche celui qui le suivait.
 */
export const mergeDeepFocusOrder = (
    { mailIds, displayedMailId }: DeepFocusOrder,
    liveMails: Email[],
): DeepFocusOrder => {
    const liveIds = uniqueIdsOf(liveMails);
    const liveIdSet = new Set(liveIds);
    const knownIdSet = new Set(mailIds);

    const keptIds = mailIds.filter((id) => liveIdSet.has(id));
    const lastKnownLivePosition = liveIds.reduce(
        (lastPosition, id, position) =>
            knownIdSet.has(id) ? position : lastPosition,
        -1,
    );
    const unknownIds = liveIds.filter((id) => !knownIdSet.has(id));
    const newFocusIds = unknownIds.filter(
        (id) => liveIds.indexOf(id) < lastKnownLivePosition,
    );
    const nextPageIds = unknownIds.filter(
        (id) => liveIds.indexOf(id) > lastKnownLivePosition,
    );
    const mergedIds = [...newFocusIds, ...keptIds, ...nextPageIds];

    if (displayedMailId && liveIdSet.has(displayedMailId)) {
        return { mailIds: mergedIds, displayedMailId };
    }

    const displayedPosition = displayedMailId
        ? mailIds.indexOf(displayedMailId)
        : -1;
    const followingId = mailIds
        .slice(displayedPosition + 1)
        .find((id) => liveIdSet.has(id));
    const precedingId = mailIds
        .slice(0, Math.max(displayedPosition, 0))
        .reverse()
        .find((id) => liveIdSet.has(id));

    return {
        mailIds: mergedIds,
        displayedMailId: followingId ?? precedingId ?? mergedIds[0],
    };
};
